"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateNewFocus = exports.getGroupedOptions = exports.iconColours = void 0;
const Select_models_1 = require("../models/Select.models");
/**
 *
 * @param isDisabled boolean
 * @param isFocused boolean
 * @param isHovered boolean
 * @param isError boolean
 * @returns string
 * @description returns a different colour depending on properties passed to it
 */
const iconColours = (isDisabled, isFocused, isHovered, isError) => {
    if (isDisabled) {
        return "stone";
    }
    if (isError) {
        return "red";
    }
    if (isFocused || isHovered) {
        return "blue";
    }
    return "zinc";
};
exports.iconColours = iconColours;
/**
 *
 * @param a GroupedOption
 * @param b GroupedOption
 * @param defaultHeader string
 * @returns string
 * @description comparator for sorting the grouping under the defaultHeader to the front of the list and preserving the order of the rest of the groupings
 */
function compareHeadings(a, b, defaultHeader) {
    //
    if (a.heading === defaultHeader) {
        return -1;
    }
    if (b.heading === defaultHeader) {
        return 1;
    }
    return 0;
}
/**
 *
 * @param splitSearchTerms string - words in search term to check match against. If searchMode is Exact this should
 * be just one element containing the whole search term
 * @param option option - option to check match against. Note: Search is ignored for label that are JSX.element
 * @param searchMode - what sort of search we want to apply
 * @returns boolean
 * @description returns a boolean for if any of the words in the search input match either the option name or the group name for an option
 */
const checkTermMatch = (splitSearchTerms, option, searchMode) => {
    switch (searchMode) {
        case Select_models_1.SearchSelectMode.AnyWord:
            // For any word search mode, check that any word matches
            return splitSearchTerms.some((term) => foundTermInOption(option, term, false));
        case Select_models_1.SearchSelectMode.IncludeValue:
            // Check that all words match. Include the option value as well as the option label
            return splitSearchTerms.every((term) => foundTermInOption(option, term, true));
        case Select_models_1.SearchSelectMode.AllWords:
        case Select_models_1.SearchSelectMode.Exact:
        default:
            // For all word search mode, check that all words match.
            // We can do the same for exact as splitSearchTerms should be one entry with the whole search term
            return splitSearchTerms.every((term) => foundTermInOption(option, term, false));
    }
};
const foundTermInOption = (option, term, includeValue) => {
    var _a, _b, _c;
    const lowerCaseTerm = term.toLowerCase();
    const foundInLabel = (_a = option.label) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(lowerCaseTerm);
    const foundInGroupHeading = !!((_b = option.grouping) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes(lowerCaseTerm));
    // Exclude the value from search if 'shouldHideValue' is true
    const foundInValue = includeValue &&
        !option.shouldHideValue &&
        ((_c = option.value) === null || _c === void 0 ? void 0 : _c.toLowerCase().includes(lowerCaseTerm));
    return foundInLabel || foundInGroupHeading || foundInValue;
};
/**
 *
 * @param options Option[] - list of options, optionally will have the .grouping field provided
 * @param defaultHeader string - group header for options without a grouping field provided
 * @param searchInput string (optional) - a search term which the options need to match
 * @param searchMode - what sort of search we want to apply
 * @returns GroupedOption[]
 * @description returns an list of grouped options based on groupings provided for the list of passed in options
 */
const getGroupedOptions = (options, defaultHeader, searchMode, searchInput) => {
    // Form an object keyed under each heading
    const groupedOptionsObject = {};
    const termsToSearch = searchInput !== undefined && searchInput !== ""
        ? searchMode === Select_models_1.SearchSelectMode.Exact
            ? [searchInput] // If exact mode use only the original term, otherwise split into words
            : searchInput.split(" ").filter((x) => x !== "")
        : null;
    options.forEach((option) => {
        // If no terms to search, show the whole dropdown, otherwise check the term matches
        if (termsToSearch === null ||
            checkTermMatch(termsToSearch, option, searchMode)) {
            (groupedOptionsObject[option.grouping || defaultHeader] =
                groupedOptionsObject[option.grouping || defaultHeader] ||
                    []).push(option);
        }
    });
    // Now flatten down into an array for rendering out
    const groupedOptions = [];
    for (const [key, value] of Object.entries(groupedOptionsObject)) {
        const newGroupedOption = {
            heading: key,
            options: value,
        };
        groupedOptions.push(newGroupedOption);
    }
    // Sort ungrouped section to the front
    groupedOptions.sort((a, b) => compareHeadings(a, b, defaultHeader));
    return groupedOptions;
};
exports.getGroupedOptions = getGroupedOptions;
/**
 *
 * @param groupedOptions GroupedOption[] - list of grouped options
 * @description returns an list of options pulled out from each group
 */
const getOptionsListFromGroups = (groupedOptions) => {
    const optionsList = [];
    groupedOptions.forEach((group) => {
        group.options.forEach((option) => {
            optionsList.push(option);
        });
    });
    return optionsList;
};
/**
 *
 * @param groupedOptions GroupedOption[] - list of grouped options
 * @param currentFocusIndex number  - current focus index
 * @param offset number - number by which to increment the focus
 * @param isSearch boolean - search mode (options will have different indexes)
 * @param theme? SelectVariant - overall theme of the component
 * @description calculates the new focus index in an options list given an offset (accounts for the start and end of the list)
 */
const calculateNewFocus = (groupedOptions, currentFocusIndex, offset, isSearch, theme = "DEFAULT") => {
    const listLengthIsGreaterByOne = isSearch || theme === "MULTISELECTALL";
    const numOptions = getOptionsListFromGroups(groupedOptions).length;
    // Calculate initial new focus
    let newFocus = currentFocusIndex + offset;
    // Don't let this go off the top or bottom of the list (top will have index 1, bottom will have index options.length + 1 if in search or multiselectall, and options.length if otherwise)
    newFocus = Math.max(1, newFocus);
    newFocus = Math.min(newFocus, listLengthIsGreaterByOne ? numOptions + 1 : numOptions);
    // Skip past any disabled options in the offset direction
    const sortedOptions = getOptionsListFromGroups(groupedOptions); // need this sorted in the order it will show in the UI
    const option = sortedOptions[listLengthIsGreaterByOne ? newFocus - 2 : newFocus - 1];
    if (option === null || option === void 0 ? void 0 : option.disabled) {
        newFocus = offset > 0 ? newFocus + 1 : newFocus - 1;
    }
    return newFocus;
};
exports.calculateNewFocus = calculateNewFocus;
//# sourceMappingURL=helpers.js.map